import $ from 'jquery';

/**
 * UserAvatarCropper - A component for handling avatar image cropping
 */
class UserAvatarCropper {
    /**
     * Get the upload modal template
     * @returns {string} HTML template
     */
    static uploadModalTemplate(model) {
        return `
        <!-- Avatar Upload Modal -->
        <div class="modal avatar-upload-modal" id="avatar-upload-modal">
            <div class="modal-background"></div>
            <div class="modal-content">
                <div class="avatar-upload">
                    <div class="avatar-upload__header">
                        <h2 class="avatar-upload__title" data-lang="avatar-upload-title">${$trans('avatar-upload-title')}</h2>
                    </div>

                    <div class="avatar-upload__content">
                        <div class="avatar-upload__dropzone">
                            <div class="avatar-upload__placeholder">
                                <img src="/textures/icons/icon_image.svg" alt="Upload placeholder" class="avatar-upload__placeholder-img">
                            </div>
                        </div>
                        <input type="file" class="avatar-upload__input" accept="image/*" style="display: none;">
                        <div class="avatar-upload__requirements" data-lang="avatar-upload-requirements">${$trans('avatar-upload-requirements')}</div>
                    </div>

                    <div class="avatar-upload__footer">
                        <button class="avatar-upload__btn avatar-upload__btn--cancel" data-lang="cancel-button">${$trans('cancel-button')}</button>
                        <button class="avatar-upload__btn avatar-upload__btn--upload" data-lang="upload-photo-button">${$trans('upload-photo-button')}</button>
                    </div>
                </div>
            </div>
        </div>
        `;
    }

    /**
     * Get the crop modal template
     * @returns {string} HTML template
     */
    static cropModalTemplate(model) {
        return `
        <!-- Avatar Crop Modal -->
        <div class="modal avatar-crop-modal" id="avatar-crop-modal">
            <div class="modal-background"></div>
            <div class="modal-content">
                <div class="avatar-crop">
                    <div class="avatar-crop__header">
                        <h2 class="avatar-crop__title" data-lang="avatar-crop-title">${$trans('avatar-crop-title')}</h2>
                    </div>

                    <div class="avatar-crop__content">
                        <div class="avatar-crop__wrapper">
                            <div id="avatar-crop-container"></div>
                        </div>
                        <div class="avatar-crop__controls">
                            <div class="avatar-crop__zoom-control">
                                <span class="avatar-crop__zoom-icon material-icons md-24">zoom_out</span>
                                <input type="range" class="avatar-crop__zoom-slider" min="0" max="100" value="0">
                                <span class="avatar-crop__zoom-icon material-icons md-24">zoom_in</span>
                            </div>
                        </div>
                    </div>

                    <div class="avatar-crop__footer">
                        <button class="avatar-crop__btn avatar-crop__btn--cancel" data-lang="cancel-button">${$trans('cancel-button')}</button>
                        <button class="avatar-crop__btn avatar-crop__btn--save" data-lang="save-button">${$trans('save-button')}</button>
                    </div>
                </div>
            </div>
        </div>
        `;
    }

    /**
     * Render the component templates to the document
     * @param {Object} model - The model containing language data
     * @param {string} targetSelector - The selector where to append the templates
     */
    static render(model, targetSelector = 'body') {
        // Check if modals already exist
        if ($('#avatar-upload-modal').length === 0) {
            $(targetSelector).append(this.uploadModalTemplate(model));
        }

        if ($('#avatar-crop-modal').length === 0) {
            $(targetSelector).append(this.cropModalTemplate(model));
        }
    }

    /**
     * Constructor
     * @param {Object} config - Configuration object
     * @param {Object} model - The model containing language data
     * @param {Object} config.api - API endpoints
     * @param {string} config.api.temporaryFileUploadUrl - URL for temporary file upload
     * @param {string} config.api.addImageUrl - URL for processing the image
     * @param {string} config.baseWebUrl - Base web URL for constructing full URLs
     * @param {Function} config.onSuccess - Callback function when avatar is successfully updated
     */
    constructor(config, model) {
        this.config = config;
        this.model = model;
        this.cropper = null;
        this.selectedFile = null;

        // Bind methods
        this.handleFileSelect = this.handleFileSelect.bind(this);
        this.initCropper = this.initCropper.bind(this);
        this.uploadAvatarFile = this.uploadAvatarFile.bind(this);
        this.processUploadedAvatar = this.processUploadedAvatar.bind(this);
        this.updateAvatarImage = this.updateAvatarImage.bind(this);
        this.showUploadError = this.showUploadError.bind(this);
        this.resetCropModal = this.resetCropModal.bind(this);
        this.resetUploadModal = this.resetUploadModal.bind(this);
    }

    /**
     * Initialize the component
     */
    init() {
        // Render templates if they don't exist
        UserAvatarCropper.render(this.model);

        // Setup event listeners
        this.setupEventListeners();
    }

    /**
     * Set up event listeners
     */
    setupEventListeners() {
        // File input change event
        $('.avatar-upload__input').on('change', (e) => {
            if (e.target.files && e.target.files[0]) {
                this.handleFileSelect(e.target.files[0]);
            }
        });

        // Set up dropzone for avatar upload
        $('.avatar-upload__dropzone').on('click', () => {
            $('.avatar-upload__input').trigger('click');
        });

        $('.avatar-upload__dropzone').on('dragover dragenter', (e) => {
            e.preventDefault();
            e.stopPropagation();
            $('.avatar-upload__dropzone').addClass('is-dragover');
        });

        $('.avatar-upload__dropzone').on('dragleave dragend drop', (e) => {
            e.preventDefault();
            e.stopPropagation();
            $('.avatar-upload__dropzone').removeClass('is-dragover');
        });

        $('.avatar-upload__dropzone').on('drop', (e) => {
            e.preventDefault();
            e.stopPropagation();

            if (e.originalEvent.dataTransfer && e.originalEvent.dataTransfer.files.length) {
                this.handleFileSelect(e.originalEvent.dataTransfer.files[0]);
            }
        });

        // Handle upload button click
        $('.avatar-upload__btn--upload').on('click', () => {
            $('.avatar-upload__input').trigger('click');
        });

        // Close upload modal when cancel button is clicked
        $('.avatar-upload__btn--cancel').on('click', () => {
            this.hideUploadModal();
        });

        // Handle zoom slider change
        $('.avatar-crop__zoom-slider').on('input', (e) => {
            if (this.cropper) {
                const zoomValue = parseFloat(e.target.value);
                // Map 0-100 to 0-1 zoom
                const zoomRatio = zoomValue / 100;
                this.cropper.zoomTo(zoomRatio);
            }
        });

        // Handle zoom in button click
        $('.avatar-crop__zoom-icon').eq(1).on('click', () => {
            if (this.cropper) {
                const currentZoom = this.cropper.getImageData().ratio;
                const newZoom = Math.min(currentZoom + 0.1, 1); // Max zoom is 1
                this.cropper.zoomTo(newZoom);

                // Update slider value
                const sliderValue = newZoom * 100; // Convert to 0-100 range
                $('.avatar-crop__zoom-slider').val(sliderValue);
            }
        });

        // Handle zoom out button click
        $('.avatar-crop__zoom-icon').eq(0).on('click', () => {
            if (this.cropper) {
                const currentZoom = this.cropper.getImageData().ratio;
                const newZoom = Math.max(currentZoom - 0.1, 0); // Min zoom is 0 (original size)
                this.cropper.zoomTo(newZoom);

                // Update slider value
                const sliderValue = newZoom * 100; // Convert to 0-100 range
                $('.avatar-crop__zoom-slider').val(sliderValue);
            }
        });

        // Handle cancel button click
        $('.avatar-crop__btn--cancel').on('click', () => {
            this.hideCropModal();
        });

        // Handle save button click
        $('.avatar-crop__btn--save').on('click', () => {
            if (this.cropper && this.selectedFile) {
                // Get cropped canvas
                const canvas = this.cropper.getCroppedCanvas({
                    width: 256,
                    height: 256
                });

                // Convert canvas to blob
                canvas.toBlob((blob) => {
                    // Create a new File object
                    const croppedFile = new File([blob], this.selectedFile.name, {
                        type: this.selectedFile.type,
                        lastModified: Date.now()
                    });

                    // Upload the cropped file
                    this.uploadAvatarFile(croppedFile);

                    // Close the modal
                    this.hideCropModal();
                }, this.selectedFile.type);
            }
        });

        // Close modals when clicking on modal background
        $('.modal-background').on('click', function() {
            const $modal = $(this).closest('.modal');

            if ($modal.hasClass('avatar-upload-modal')) {
                this.hideUploadModal();
            } else if ($modal.hasClass('avatar-crop-modal')) {
                this.hideCropModal();
            }
        }.bind(this));
    }

    /**
     * Handle file selection
     * @param {File} file - The selected file
     */
    handleFileSelect(file) {
        // Check file type
        const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
        if (!validTypes.includes(file.type)) {
            this.showUploadError($trans('avatar-upload-error-type'));
            return;
        }

        // Check file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            this.showUploadError($trans('avatar-upload-error-size'));
            return;
        }

        // Create a FileReader to read the image dimensions
        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                // Check image dimensions (min 250x250)
                if (img.width < 250 || img.height < 250) {
                    this.showUploadError($trans('avatar-upload-error-dimensions'));
                    return;
                }

                // Store the selected file
                this.selectedFile = file;

                // Close upload modal and open crop modal
                this.hideUploadModal();

                // Show crop modal
                this.showCropModal();
            };
            img.src = e.target.result;
        };
        reader.readAsDataURL(file);
    }

    /**
     * Initialize the cropper
     * @param {string} imageSrc - The image source URL
     */
    initCropper(imageSrc) {
        // Create image element
        const image = document.createElement('img');
        image.src = imageSrc;
        image.id = 'crop-image';

        // Add image to container
        const container = document.getElementById('avatar-crop-container');
        container.innerHTML = '';
        container.appendChild(image);

        // Initialize Cropper.js
        this.cropper = new Cropper(image, {
            aspectRatio: 1,
            viewMode: 3,
            dragMode: 'move',
            autoCropArea: 1,
            restore: false,
            guides: false,
            center: true,
            highlight: false,
            cropBoxMovable: false,
            cropBoxResizable: false,
            toggleDragModeOnDblclick: false,
            ready: () => {
                // Reset zoom slider to 0 (full image)
                $('.avatar-crop__zoom-slider').val(0);

                // Set initial zoom to fit the image fully
                this.cropper.zoomTo(0);
            }
        });
    }

    /**
     * Upload the avatar file
     * @param {File} file - The file to upload
     */
    uploadAvatarFile(file) {
        // Generate a UUID for the file
        const uuid = this.generateUUID();
        const fileExtension = file.name.split('.').pop();
        const fileNameOnServer = uuid + '.' + fileExtension;

        // Create FormData
        const formData = new FormData();
        formData.append('UploadForm[file]', file);

        // Add required Dropzone fields
        formData.append('dzuuid', uuid);
        formData.append('dzchunkindex', 0);
        formData.append('dztotalfilesize', file.size);
        formData.append('dzchunksize', file.size);
        formData.append('dztotalchunkcount', 1);
        formData.append('dzchunkbyteoffset', 0);

        // Show loading state
        $('.profile-info__avatar').addClass('is-loading');

        // Step 1: Upload the file to temporary storage
        fetch(this.config.api.temporaryFileUploadUrl, {
            method: 'POST',
            body: formData,
            headers: {
                'X-CSRF-Token': window.yii.getCsrfToken()
            }
        })
        .then(response => response.json())
        .then(data => {
            if (data && data.success) {
                // Step 2: Process the uploaded file
                this.processUploadedAvatar(fileNameOnServer);
            } else {
                this.showUploadError($trans('avatar-upload-error-upload'));
            }
        })
        .catch(error => {
            this.showUploadError($trans('avatar-upload-error-upload'));
        })
        .finally(() => {
            // Hide loading state
            $('.profile-info__avatar').removeClass('is-loading');
        });
    }

    /**
     * Process the uploaded avatar
     * @param {string} fileNameOnServer - The filename on the server
     */
    processUploadedAvatar(fileNameOnServer) {
        // Ensure fileNameOnServer is not null or undefined
        if (!fileNameOnServer) {
            this.showUploadError($trans('avatar-upload-error-upload'));
            return;
        }

        // Call the API to add the image
        fetch(this.config.api.addImageUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': window.yii.getCsrfToken()
            },
            body: JSON.stringify({
                fileNameOnServer: fileNameOnServer,
                forceJpg: 'avatar',
                sliceImage: false
            })
        })
        .then(response => {
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            return response.json();
        })
        .then(data => {
            if (data && data.url) {
                // Update avatar image
                const fullUrl = this.config.baseWebUrl + data.url;
                this.updateAvatarImage(fullUrl);

                // Call success callback if provided
                if (typeof this.config.onSuccess === 'function') {
                    this.config.onSuccess(fullUrl);
                }
            } else {
                this.showUploadError($trans('avatar-upload-error-process'));
            }
        })
        .catch(error => {
            this.showUploadError($trans('avatar-upload-error-process'));
        });
    }

    /**
     * Update the avatar image
     * @param {string} url - The new avatar URL
     */
    updateAvatarImage(url) {
        // Update avatar in profile
        $('.profile-info__avatar-img').attr('src', url);

        // Update avatar in header
        $('.user-avatar img').attr('src', url);

        // Update avatar in profile modal
        $('.local-avatar').attr('src', url);
        $('#chosen-avatar').attr('src', url);
    }

    /**
     * Show upload error message
     * @param {string} message - The error message
     */
    showUploadError(message) {
        // Check if error element exists, if not create it
        let errorElement = $('.avatar-upload__error');
        if (errorElement.length === 0) {
            errorElement = $('<div class="avatar-upload__error"></div>');
            $('.avatar-upload__content').append(errorElement);
        }

        // Show error message
        errorElement.text(message).addClass('is-visible');
        $('.avatar-upload__dropzone').addClass('has-error');

        // Hide error after 3 seconds
        setTimeout(() => {
            errorElement.removeClass('is-visible');
            $('.avatar-upload__dropzone').removeClass('has-error');
        }, 3000);
    }

    /**
     * Reset the crop modal
     */
    resetCropModal() {
        if (this.cropper) {
            this.cropper.destroy();
            this.cropper = null;
        }

        document.getElementById('avatar-crop-container').innerHTML = '';
        this.selectedFile = null;
    }

    /**
     * Reset the upload modal
     */
    resetUploadModal() {
        // Reset file input
        $('.avatar-upload__input').val('');

        // Remove error message
        $('.avatar-upload__error').removeClass('is-visible');
        $('.avatar-upload__dropzone').removeClass('has-error');
    }

    /**
     * Show the upload modal
     */
    showUploadModal() {
        $('#avatar-upload-modal').addClass('is-active');
    }

    /**
     * Hide the upload modal
     */
    hideUploadModal() {
        $('#avatar-upload-modal').removeClass('is-active');
        this.resetUploadModal();
    }

    /**
     * Show the crop modal
     */
    showCropModal() {
        $('#avatar-crop-modal').addClass('is-active');

        // Initialize cropper when modal is shown
        if (this.selectedFile) {
            this.initCropper(URL.createObjectURL(this.selectedFile));
        }
    }

    /**
     * Hide the crop modal
     */
    hideCropModal() {
        $('#avatar-crop-modal').removeClass('is-active');
        this.resetCropModal();
    }

    /**
     * Generate a UUID v4
     * @returns {string} UUID
     */
    generateUUID() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }
}

export default UserAvatarCropper;
